import { type ActivePlanRes, IntervalUnits } from '@on3/api/src';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Brands } from '@on3/ui-lib/components/Brands';
import { Link } from '@on3/ui-lib/components/Link';
import { RecruitsOrderSummary } from '@on3/ui-lib/components/Modal/User/Join/Recruits/OrderSummary';
import { ServiceLogo } from '@on3/ui-lib/components/ServiceLogo/ServiceLogo';
import { useSubscribe } from '@on3/ui-lib/components/Subscription/Provider';
import { On3Recruit } from '@on3/ui-lib/components/Svg';
import { useAuth, useSite } from '@on3/ui-lib/index';
import { type PurchaseModalProps } from '@on3/ui-lib/src/types/purchase';
import { formatCurrency } from '@on3/ui-lib/utils/recurly';
import { trackGoogle } from '@on3/ui-lib/utils/tracking';
import { CheckoutForm } from '@public/components/Subscription/CheckoutForm';
import { createDescription } from '@public/utils/meta';
import { type CheckoutPrice } from '@recurly/recurly-js';
import { useEffect, useMemo, useState } from 'react';

import styles from './UserJoin.module.scss';

export const UserJoinModal = ({
  context,
  planKey,
  couponKey,
  recruit,
  onAuth,
}: PurchaseModalProps) => {
  const { currentSite } = useSite();
  const { user } = useAuth();
  const {
    isSubscribedToSite,
    osKey: offerSetKey,
    osSite,
    getPlanById,
  } = useSubscribe();

  const site = osSite || currentSite;

  const { key: siteKey, name: siteName, defaultAssetRes: siteAsset } = site;

  const isRecruits = siteKey === 409;

  const [pricing, setPricing] = useState<CheckoutPrice | null>(null);

  useEffect(() => {
    if (!user?.uid) return;

    trackGoogle('begin_checkout', {
      planKey,
      siteKey: currentSite.key,
      siteName: currentSite.name,
      userKey: user?.uid,
    });
  }, [currentSite.key, currentSite.name, planKey, user?.uid]);

  const plan: ActivePlanRes | undefined = useMemo(
    () => getPlanById(planKey),
    [getPlanById, planKey],
  );

  // @TODO: recurly - Find a coupon by couponKey when implemented.

  /**
   * Plan details
   * - planName: If the plan is a recruit plan, use the plan name only. Otherwise, prefix with the site name.
   * - planIntervals: Sorted list of pricing intervals for the plan.
   * - planInitialPricing: The initial pricing amount from the first interval.
   * - planSetupFee: Any setup fee associated with the plan.
   * - planIntervalUnit: The billing interval unit (e.g., month, year).
   * - planTrial: Trial period details if available.
   * - lineItems: Breakdown of charges including setup fee, trial, and taxes.
   */
  const planName = plan?.code?.includes('recruits')
    ? plan.name
    : `${siteName} ${plan?.name}`;

  const planIntervals = useMemo(() => {
    if (!plan?.rampIntervals) return [];

    const sorted = plan.rampIntervals.sort(
      (a, b) => (a.startingBillingCycle || 0) - (b.startingBillingCycle || 0),
    );
    const formatted = sorted.map((interval) => ({
      amount: interval.currencies?.[0]?.unitAmount || 0,
    }));

    return formatted;
  }, [plan]);

  const planInterval = plan?.intervalLength || 1;
  const planIntervalUnit =
    plan?.intervalUnit === IntervalUnits.Months
      ? 'month'
      : plan?.intervalUnit === IntervalUnits.Days
        ? 'day'
        : 'period';

  const planIntervalLabel =
    planInterval === 12 && planIntervalUnit === 'month'
      ? 'year'
      : planIntervalUnit;

  const planInitialPricing = planIntervals?.length
    ? planIntervals[0].amount
    : 0;

  const planSetupFee = plan?.setupFees?.find((fee) => fee.currency === 'USD')
    ?.unitAmount;

  const planTrialLength = plan?.trialLength || 0;
  const planTrialUnit = plan?.trialUnit?.toLowerCase() || '';
  const planTrial =
    planTrialLength && planTrialUnit
      ? `${planTrialLength} ${planTrialUnit}`
      : null;

  const lineItems = [
    {
      key: 'taxes',
      name: 'Sales Tax',
      amount: pricing?.now?.taxes !== '0.00' ? pricing?.now?.taxes : null,
      description: 'Calculated at checkout',
    },
  ] as {
    key: string;
    name: string;
    amount?: number | string;
    description?: string;
  }[];

  if (planSetupFee) {
    lineItems.unshift({
      key: planTrial ? 'trial' : 'setup-fee',
      name: planTrial ? `Trial — ${planTrial}` : 'Setup Fee',
      amount: planSetupFee ? planSetupFee : 0,
    });
  } else if (planTrial) {
    lineItems.unshift({
      key: 'trial',
      name: `Trial — ${planTrial}`,
      description: 'Included',
    });
  }

  const paymentNotes = useMemo(() => {
    const notes = ['Sales tax may apply. Cancel anytime.'] as string[];

    switch (true) {
      case !planTrial && planIntervals.length === 1:
        notes.unshift(
          createDescription(
            'Billed at {0} per {1}.',
            formatCurrency(planInitialPricing),
            planIntervalLabel,
          ),
        );
        break;
      case !!planTrial && !!planSetupFee && planIntervals.length === 1:
        notes.unshift(
          createDescription(
            '{0} for {1}, then automatically renews at {2} per {3}.',
            formatCurrency(planSetupFee),
            planTrial,
            formatCurrency(planInitialPricing),
            planIntervalLabel,
          ),
        );
        break;
      case !!planTrial && !!planSetupFee && planIntervals.length > 1:
        notes.unshift(
          createDescription(
            '{0} for {1}, then {2} for {3}, then automatically renews at {4} per {5}.',
            formatCurrency(planSetupFee),
            planTrial,
            formatCurrency(planInitialPricing),
            planIntervalLabel,
            formatCurrency(planIntervals[1]?.amount || planInitialPricing),
            planIntervalLabel,
          ),
        );
        break;
      case planIntervals.length === 2:
        notes.unshift(
          createDescription(
            'Billed at {0}, then {1} per {2}.',
            formatCurrency(planInitialPricing),
            formatCurrency(planIntervals[1]?.amount || planInitialPricing),
            planIntervalLabel,
          ),
        );
        break;
    }

    return notes.join(' ');
  }, [
    planInitialPricing,
    planIntervalLabel,
    planIntervals,
    planSetupFee,
    planTrial,
  ]);

  return (
    <div className={styles.block}>
      <div className={styles.banner}>
        {isRecruits ? (
          <On3Recruit fill="#fff" />
        ) : siteAsset ? (
          <Avatar
            altText={siteAsset?.altText || 'Site Logo'}
            domain={siteAsset?.domain || ''}
            height={32}
            source={siteAsset?.source || ''}
            teamAvatar
          />
        ) : (
          <div className={styles.bannerChannel}>
            <ServiceLogo type="on3" />
            <span>{siteName}</span>
          </div>
        )}
      </div>
      {plan && (
        <div className={styles.container}>
          <h1 className={styles.heading}>Checkout</h1>
          {isSubscribedToSite && (
            <p className={styles.subscribedAlert}>
              You are already subscribed to {siteName}. Please contact support
              to change your plan, or go to{' '}
              <Link href="/my/settings/?tab=subscriptions">
                your subscription settings
              </Link>
              .
            </p>
          )}
          <div className={styles.inner}>
            <div className={styles.summary}>
              <div className={styles.details}>
                <span className={styles.title}>Order Summary</span>
                {isRecruits ? (
                  <RecruitsOrderSummary recruit={recruit} />
                ) : (
                  <dl className={styles.plan}>
                    <dt className={styles.name}>{planName}</dt>
                    <dd>
                      {planIntervals?.length
                        ? formatCurrency(planInitialPricing)
                        : '-'}
                    </dd>
                  </dl>
                )}
                {!!lineItems.length && (
                  <div className={styles.lineItems}>
                    {lineItems.map((item, i) => (
                      <dl className={styles.lineItem} key={i}>
                        <dt className={styles.name}>{item.name}</dt>
                        <dd>
                          {item.amount
                            ? formatCurrency(item.amount)
                            : item.description}
                        </dd>
                      </dl>
                    ))}
                  </div>
                )}
                <dl className={styles.totalCharged}>
                  <dt>Total</dt>
                  <dd>{pricing ? `$${pricing.now?.total}` : '-'}</dd>
                </dl>
                <p className={styles.notes}>{paymentNotes}</p>
              </div>
            </div>
            <div className={styles.form}>
              <Brands className={styles.brands} />
              {!!plan.code && (
                <CheckoutForm
                  athlete={recruit}
                  context={context}
                  offerSetKey={offerSetKey}
                  onAuth={onAuth}
                  planCode={plan.code}
                  setPricing={setPricing}
                  siteContext={site}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
